/**
 * IndexedDB-backed KeyValueStore.
 *
 * Chosen over localStorage because it holds megabytes of message history and
 * attachments without blocking the main thread, and because it survives the
 * eviction rules that hit localStorage first under storage pressure.
 */
import type { KeyValueStore } from '@vav/client-core';
import { databaseNameFor } from './profiles.ts';

const STORE = 'kv';

function open(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export class IndexedDbStore implements KeyValueStore {
  #db?: Promise<IDBDatabase>;
  #name: string;

  /** One database per profile; see `databaseNameFor`. */
  constructor(dbName: string) {
    this.#name = dbName;
  }

  #connect(): Promise<IDBDatabase> {
    this.#db ??= open(this.#name);
    return this.#db;
  }

  async #tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => Promise<T>): Promise<T> {
    const db = await this.#connect();
    const tx = db.transaction(STORE, mode);
    const result = await fn(tx.objectStore(STORE));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return result;
  }

  async get(key: string): Promise<string | undefined> {
    return this.#tx('readonly', async (store) => (await promisify(store.get(key))) as string | undefined);
  }

  async set(key: string, value: string): Promise<void> {
    await this.#tx('readwrite', async (store) => {
      await promisify(store.put(value, key));
    });
  }

  async remove(key: string): Promise<void> {
    await this.#tx('readwrite', async (store) => {
      await promisify(store.delete(key));
    });
  }

  async keys(prefix: string): Promise<string[]> {
    // A bounded key range beats scanning every key: history grows without limit
    // and a full scan on each thread open would get slow fast.
    const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
    return this.#tx('readonly', async (store) => {
      const keys = (await promisify(store.getAllKeys(range))) as IDBValidKey[];
      return keys.map(String);
    });
  }

  async clear(): Promise<void> {
    await this.#tx('readwrite', async (store) => {
      await promisify(store.clear());
    });
  }
}

/** Falls back to memory when IndexedDB is unavailable (private mode, old iOS). */
export async function createStore(profileId: string): Promise<KeyValueStore> {
  try {
    const store = new IndexedDbStore(databaseNameFor(profileId));
    await store.get('__probe__');
    return store;
  } catch {
    const { MemoryStore } = await import('@vav/client-core');
    console.warn('[vav] IndexedDB unavailable — falling back to memory (history will not persist)');
    return new MemoryStore();
  }
}
